export interface Me {
  uid: string;
  email: string;
  name: string | null;
  onboarded: boolean; // name + sport + skill captured (computed server-side)
  onboarding?: { completed: number; total: number }; // visible-step progress (name/sport/skill)
  impersonating?: boolean; // this is an admin "login as" session
}

// Return to your own admin account from a "login as" session.
export async function stopImpersonating(): Promise<void> {
  const res = await fetch("/api/admin/stop-impersonating", {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`stop-impersonating failed: ${res.status}`);
}

export async function getMe(): Promise<Me | null> {
  const res = await fetch("/api/auth/me", { credentials: "include" });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error(`auth/me failed: ${res.status}`);
  return (await res.json()) as Me;
}

export async function requestMagicLink(
  email: string,
  next?: string,
): Promise<void> {
  const res = await fetch("/api/auth/magic-link", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, next }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error ?? `magic-link failed: ${res.status}`);
  }
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
}

export interface SmsConsent {
  phone_e164: string | null;
  consent: boolean;
}

export async function getSmsConsent(): Promise<SmsConsent> {
  const res = await fetch("/api/me/sms", { credentials: "include" });
  if (!res.ok) throw new Error(`sms get failed: ${res.status}`);
  return (await res.json()) as SmsConsent;
}

export async function dismissSmsPrompt(): Promise<void> {
  await fetch("/api/me/sms/dismiss", { method: "POST", credentials: "include" });
}

export type Channel = "email" | "sms" | "both";

export interface NotificationPrefs {
  phone_e164: string | null;
  consent: boolean;
  channel_sessions: Channel;
  channel_beacons: Channel;
  toggles: Record<string, boolean>;
}

export async function getNotifications(): Promise<NotificationPrefs> {
  const res = await fetch("/api/me/notifications", { credentials: "include" });
  if (!res.ok) throw new Error(`notifications get failed: ${res.status}`);
  return (await res.json()) as NotificationPrefs;
}

export async function setNotifications(input: {
  channel_sessions?: Channel;
  channel_beacons?: Channel;
  toggles?: Record<string, boolean>;
}): Promise<NotificationPrefs> {
  const res = await fetch("/api/me/notifications", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `notifications update failed: ${res.status}`);
  }
  return (await res.json()) as NotificationPrefs;
}

// Admin: email THIS game's Yes/No prompt to members who haven't RSVP'd yet
// (no LLM). `reminded` = how many got the nudge; the rest already answered.
export async function remindSession(
  sessionId: string,
): Promise<{ reminded?: number; skipped_responded?: number }> {
  const res = await fetch(`/api/sessions/${sessionId}/remind`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `remind failed: ${res.status}`);
  }
  return (await res.json()) as { reminded?: number; skipped_responded?: number };
}

export async function setSmsConsent(input: {
  phone: string;
  consent: boolean;
}): Promise<SmsConsent> {
  const res = await fetch("/api/me/sms", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `sms update failed: ${res.status}`);
  }
  return (await res.json()) as SmsConsent;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string | null;
  sport: string | null;
  skill: number | null;
  timezone: string | null;
  group_count: number;
  created_at: string | null;
  last_seen_at: string | null;
}

export async function adminListUsers(): Promise<{ users: AdminUser[] } | "forbidden" | "notfound"> {
  const res = await fetch("/api/admin/users", { credentials: "include" });
  if (res.status === 403) return "forbidden";
  if (res.status === 404) return "notfound";
  if (!res.ok) throw new Error(`admin/users failed: ${res.status}`);
  return (await res.json()) as { users: AdminUser[] };
}

export interface AdminEmail {
  id: string;
  direction: "in" | "out";
  to_email: string; // recipient (out) or sender (in)
  subject: string;
  kind: string;
  status: string; // sent | failed | dev | received
  provider_message_id: string | null;
  error: string | null;
  created_at: string | null;
}

export async function adminListEmails(
  opts: { kind?: string; status?: string } = {},
): Promise<{ emails: AdminEmail[] } | "forbidden" | "notfound"> {
  const qs = new URLSearchParams();
  if (opts.kind) qs.set("kind", opts.kind);
  if (opts.status) qs.set("status", opts.status);
  const res = await fetch(`/api/admin/emails?${qs.toString()}`, { credentials: "include" });
  if (res.status === 403) return "forbidden";
  if (res.status === 404) return "notfound";
  if (!res.ok) throw new Error(`admin/emails failed: ${res.status}`);
  return (await res.json()) as { emails: AdminEmail[] };
}

export interface AdminEmailDetail extends AdminEmail {
  body_html: string | null;
  body_text: string | null;
}

export interface AdminSms {
  id: string;
  direction: "in" | "out";
  from_number: string | null;
  to_number: string;
  body: string | null;
  kind: string;
  status: string; // sent | failed | dev | received
  provider_message_id: string | null;
  error: string | null;
  segments: number | null;
  created_at: string | null;
}

export async function adminListSms(
  opts: { kind?: string; status?: string; direction?: string } = {},
): Promise<{ sms: AdminSms[] } | "forbidden" | "notfound"> {
  const qs = new URLSearchParams();
  if (opts.kind) qs.set("kind", opts.kind);
  if (opts.status) qs.set("status", opts.status);
  if (opts.direction) qs.set("direction", opts.direction);
  const res = await fetch(`/api/admin/sms?${qs.toString()}`, { credentials: "include" });
  if (res.status === 403) return "forbidden";
  if (res.status === 404) return "notfound";
  if (!res.ok) throw new Error(`admin/sms failed: ${res.status}`);
  return (await res.json()) as { sms: AdminSms[] };
}

export async function adminGetEmail(id: string): Promise<AdminEmailDetail> {
  const res = await fetch(`/api/admin/emails/${id}`, { credentials: "include" });
  if (!res.ok) throw new Error(`admin/emails/${id} failed: ${res.status}`);
  return (await res.json()) as AdminEmailDetail;
}

export async function adminRetryEmail(id: string): Promise<{ status: string }> {
  const res = await fetch(`/api/admin/emails/${id}/retry`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `retry failed: ${res.status}`);
  }
  return (await res.json()) as { status: string };
}

export interface DripRunResult {
  ok: boolean;
  groups_checked: number;
  sent: number;
  conversions: number;
  admins_checked: number;
  skipped_grace: number;
  skipped_optout: number;
  skipped_recent: number;
  skipped_complete: number;
  skipped_nothing: number;
}

export async function adminRunDrip(): Promise<DripRunResult> {
  const res = await fetch("/api/admin/drip/run", {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `drip run failed: ${res.status}`);
  }
  return (await res.json()) as DripRunResult;
}

export interface DripSendResult {
  ok: boolean;
  lesson: string;
  group_id: string;
  group_name: string;
  to_email: string;
}

export async function adminSendDrip(body: {
  user_id?: string;
  email?: string;
  lesson?: string;
  group_id?: string;
}): Promise<DripSendResult> {
  const res = await fetch("/api/admin/drip/send", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `drip send failed: ${res.status}`);
  }
  return (await res.json()) as DripSendResult;
}

export async function adminLoginAs(userId: string): Promise<{ as_user: string }> {
  const res = await fetch(`/api/admin/login-as/${userId}`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`login-as failed: ${res.status}`);
  return (await res.json()) as { ok: true; as_user: string };
}

export interface JoinResult {
  ok: true;
  already_member: boolean;
  group_id: string;
  name: string;
  sport: string;
}

export async function joinGroup(code: string): Promise<JoinResult> {
  const res = await fetch("/api/groups/join", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (res.status === 401) {
    throw new Error("unauthenticated");
  }
  if (res.status === 404) {
    throw new Error("invalid or expired invite");
  }
  if (!res.ok) {
    throw new Error(`join failed: ${res.status}`);
  }
  return (await res.json()) as JoinResult;
}

// ============================================================================
// Court-side UI read endpoints
// ============================================================================

export interface SessionDTO {
  id: string;
  group_id: string;
  group_name: string;
  sport: string;
  starts_at: string;
  ends_at: string;
  starts_at_display: string;
  location: string | null;
  capacity: number | null;
  format: "singles" | "doubles" | null;
  court_count: number | null;
  confirmed: number;
  waitlist: number;
  declined: number;
  no_reply: number;
  member_count: number;
  my_status: "confirmed" | "waitlist" | "no" | null;
  my_court: number | null;
  my_court_label: string | null;
  has_result: boolean; // a confirmed match result has been recorded
  result_count: number;
  result_summary: string | null; // e.g. "6-4 7-5" (one match) or "2 results"
  confirmed_players: ConfirmedPlayer[]; // first few confirmed (for "who's in" avatars); [] unless requested
  view_count: number; // distinct members who viewed it (excl. creator)
}

export interface ConfirmedPlayer {
  user_id: string;
  name: string;
  initials: string;
  is_you: boolean;
}

export interface CourtDTO {
  number: number;
  label: string;
  players: RsvpPersonDTO[];
  waiting_for: number;
  full: boolean;
}

export interface CourtLayoutDTO {
  session_id: string;
  format: "singles" | "doubles" | null;
  per_court: number | null;
  court_count: number | null;
  court_labels: string[] | null;
  courts: CourtDTO[];
  unassigned: RsvpPersonDTO[];
  facility_email_set: boolean;
  locked: boolean;
}

// Admin: set this session's actual court numbers/names for the week.
export async function setSessionCourtLabels(
  sessionId: string,
  labels: string[] | null,
): Promise<CourtLayoutDTO> {
  const res = await fetch(`/api/sessions/${sessionId}/court-labels`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ labels }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `set court labels failed: ${res.status}`);
  }
  return (await res.json()) as CourtLayoutDTO;
}

// Admin: manually send the facility the court request for this session. The
// server enforces the same T-24h rule as the automated lock.
export async function sendSessionFacilityRequest(
  sessionId: string,
): Promise<{ ok: boolean; facility_email: string | null }> {
  const res = await fetch(`/api/sessions/${sessionId}/facility-request`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `facility request failed: ${res.status}`);
  }
  return (await res.json()) as { ok: boolean; facility_email: string | null };
}

export async function getSessionCourts(sessionId: string): Promise<CourtLayoutDTO> {
  const res = await fetch(`/api/sessions/${sessionId}/courts`, { credentials: "include" });
  if (!res.ok) throw new Error(`sessions/${sessionId}/courts failed: ${res.status}`);
  return (await res.json()) as CourtLayoutDTO;
}

// Admin: auto-fill (FCFS) or move one player to/off a court (court_number null = unassign).
export async function assignSessionCourts(
  sessionId: string,
  body:
    | { auto: true }
    | { user_id: string; court_number: number | null },
): Promise<CourtLayoutDTO> {
  const res = await fetch(`/api/sessions/${sessionId}/courts`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `assign courts failed: ${res.status}`);
  }
  return (await res.json()) as CourtLayoutDTO;
}

// --- Ladder & match results --------------------------------------------------

export interface LadderEntryDTO {
  position: number;
  user_id: string;
  name: string;
  initials: string;
  matches_played: number;
  is_you: boolean;
}

export interface LadderDTO {
  group_id: string;
  format: "singles" | "doubles";
  entries: LadderEntryDTO[];
}

// A group's ladder for one format. Returns positions only — the underlying
// rating is intentionally hidden (see docs/standings-design.md).
export async function getGroupLadder(
  groupId: string,
  format: "singles" | "doubles",
): Promise<LadderDTO> {
  const res = await fetch(
    `/api/groups/${groupId}/ladder?format=${encodeURIComponent(format)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`ladder failed: ${res.status}`);
  return (await res.json()) as LadderDTO;
}

// Admin: record a confirmed result. Returns the refreshed ladder for that format.
export async function recordMatch(
  groupId: string,
  body: {
    format: "singles" | "doubles";
    team_a: string[];
    team_b: string[];
    winner: "a" | "b";
    session_id?: string | null;
    score?: string | null;
  },
): Promise<LadderDTO> {
  const res = await fetch(`/api/groups/${groupId}/matches`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `record result failed: ${res.status}`);
  }
  return (await res.json()) as LadderDTO;
}

export interface MatchSummaryDTO {
  id: string;
  played_at: string;
  winner: "a" | "b";
  team_a: string[]; // player names
  team_b: string[];
  score?: string | null; // optional free-text, e.g. "6-4 7-5"
  session_id?: string | null;
  session_label?: string | null; // e.g. "Sat Jun 7, 8:00 AM"; null = pickup (no session)
}

// Admin: hand-set the ladder order (strongest first). Existing matches replay on top.
export async function setLadderSeed(
  groupId: string,
  format: "singles" | "doubles",
  order: string[],
): Promise<LadderDTO> {
  const res = await fetch(`/api/groups/${groupId}/ladder/seed`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ format, order }),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `seed ladder failed: ${res.status}`);
  }
  return (await res.json()) as LadderDTO;
}

// Admin: recent recorded results for the undo UI.
export async function getRecentMatches(
  groupId: string,
  format: "singles" | "doubles",
): Promise<{ matches: MatchSummaryDTO[] }> {
  const res = await fetch(
    `/api/groups/${groupId}/matches?format=${encodeURIComponent(format)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new Error(`matches failed: ${res.status}`);
  return (await res.json()) as { matches: MatchSummaryDTO[] };
}

export interface SessionResultsDTO {
  session_id: string;
  visible: boolean;
  can_delete: boolean;
  matches: MatchSummaryDTO[];
}

// Results recorded for one session (gated by the group's sessionResults setting).
export async function getSessionResults(sessionId: string): Promise<SessionResultsDTO> {
  const res = await fetch(`/api/sessions/${sessionId}/results`, { credentials: "include" });
  if (!res.ok) throw new Error(`session results failed: ${res.status}`);
  return (await res.json()) as SessionResultsDTO;
}

// Admin: void a recorded result; ratings recompute. Returns the refreshed ladder.
export async function deleteMatch(groupId: string, matchId: string): Promise<LadderDTO> {
  const res = await fetch(`/api/groups/${groupId}/matches/${matchId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new Error(detail.detail || `delete result failed: ${res.status}`);
  }
  return (await res.json()) as LadderDTO;
}

export interface RsvpPersonDTO {
  user_id: string;
  name: string;
  initials: string;
  is_you: boolean;
}

export interface SessionRsvpBreakdownDTO {
  session_id: string;
  member_count: number;
  confirmed: RsvpPersonDTO[];
  waitlist: RsvpPersonDTO[];
  declined: RsvpPersonDTO[];
  no_reply: RsvpPersonDTO[];
}

export async function getSessionRsvps(sessionId: string): Promise<SessionRsvpBreakdownDTO> {
  const res = await fetch(`/api/sessions/${sessionId}/rsvps`, { credentials: "include" });
  if (!res.ok) throw new Error(`sessions/${sessionId}/rsvps failed: ${res.status}`);
  return (await res.json()) as SessionRsvpBreakdownDTO;
}

export interface RsvpActivityDTO {
  id: string;
  kind?: "rsvp";
  user_id: string;
  name: string;
  action: "yes" | "no" | "promoted" | "cut";
  status: "confirmed" | "waitlist" | "no";
  created_at: string;
  is_you: boolean;
}
export interface ResultActivityDTO {
  id: string;
  kind: "result";
  event: "recorded" | "deleted";
  text: string; // e.g. "Jane & Bob def. Carol & Dave · 6-4"
  by?: string | null; // who voided it (deleted events)
  created_at: string;
  is_you: boolean;
}
export interface ViewActivityDTO {
  id: string;
  kind: "view";
  user_id: string;
  name: string;
  source: "app" | "email";
  created_at: string;
  is_you: boolean;
}
// The activity timeline mixes RSVP changes, recorded results, and views.
export type RsvpEventDTO = RsvpActivityDTO | ResultActivityDTO | ViewActivityDTO;

// Append-only RSVP timeline for a session (newest-first), incl. yes→no→yes flips.
export async function getSessionRsvpHistory(sessionId: string): Promise<RsvpEventDTO[]> {
  const res = await fetch(`/api/sessions/${sessionId}/rsvp-history`, { credentials: "include" });
  if (!res.ok) throw new Error(`rsvp-history failed: ${res.status}`);
  return ((await res.json()) as { events: RsvpEventDTO[] }).events;
}

export interface BeaconDTO {
  id: string;
  group_id: string;
  group_name: string;
  creator_name: string;
  description: string | null;
  window_display: string;
  confirmed: number;
  needed: number;
  my_response: "accepted" | "declined" | null;
  is_mine: boolean; // the viewer created this beacon (outgoing request)
  expires_at: string; // ISO 8601 — when the accept window closes
  view_count: number; // distinct members who viewed it (excl. creator)
}

export interface BeaconViewActivityDTO {
  id: string;
  kind: "view";
  name: string;
  source: "app" | "email";
  created_at: string;
  is_you: boolean;
}
export interface BeaconResponseActivityDTO {
  id: string;
  kind: "response";
  name: string;
  response: "accepted" | "declined";
  created_at: string;
  is_you: boolean;
}
export type BeaconEventDTO = BeaconViewActivityDTO | BeaconResponseActivityDTO;

/** Activity feed for a beacon (newest-first): responses + views. */
export async function getBeaconActivity(beaconId: string): Promise<BeaconEventDTO[]> {
  const res = await fetch(`/api/beacons/${beaconId}/activity`, { credentials: "include" });
  if (!res.ok) throw new Error(`beacon activity failed: ${res.status}`);
  return ((await res.json()) as { events: BeaconEventDTO[] }).events;
}

/** Record that the current user viewed these sessions/beacons (fire-and-forget). */
export async function recordViews(
  items: { type: "session" | "beacon"; id: string }[],
): Promise<void> {
  if (items.length === 0) return;
  try {
    await fetch("/api/views", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
  } catch {
    /* best-effort — a missed view must never disrupt the UI */
  }
}

export interface HomeTodayDTO {
  sessions: SessionDTO[];
  recently_played: SessionDTO[]; // most recent past session per club (last 14 days)
  beacons: BeaconDTO[];
  polls: PollDTO[];
  has_clubs: boolean; // user belongs to at least one club
  is_admin_anywhere: boolean; // user is an admin of at least one club (empty-state CTA)
  show_sms_prompt: boolean; // one-time SMS opt-in nudge (not opted in, not dismissed)
  setup: SetupState; // getting-started checklist progress
  admin_nudges: unknown[];
}

export interface SetupState {
  profile: boolean;
  club: boolean;
  availability: boolean;
  rsvp: boolean;
  beacon: boolean;
  weekly: boolean; // admin-shaped task; hide for non-admins
  result: boolean;
  sms: boolean;
  is_admin: boolean;
}

export async function getHomeToday(): Promise<HomeTodayDTO> {
  const res = await fetch("/api/home/today", { credentials: "include" });
  if (!res.ok) throw new Error(`home/today failed: ${res.status}`);
  return (await res.json()) as HomeTodayDTO;
}

// --- Availability grid (recurring weekly template, mon..sun × 3 brackets) ----

export type AvailabilityBracket = "morning" | "afternoon" | "evening";
/** {"mon": ["morning","evening"], ...} — days with no brackets are omitted. */
export type AvailabilityGrid = Record<string, AvailabilityBracket[]>;

export interface GroupAvailability {
  group_id: string;
  name: string;
  /** cells[day][bracket] = names of members generally free then. */
  cells: Record<string, Record<AvailabilityBracket, string[]>>;
  members_set: number;
  members_stale: number;
  total_members: number;
}

export interface AvailabilityHome {
  mine: AvailabilityGrid;
  updated_at: string | null;
  groups: GroupAvailability[];
}

export async function getAvailabilityHome(): Promise<AvailabilityHome> {
  const res = await fetch("/api/home/availability", { credentials: "include" });
  if (!res.ok) throw new Error(`home/availability failed: ${res.status}`);
  return (await res.json()) as AvailabilityHome;
}

export async function putMyAvailability(
  grid: AvailabilityGrid,
): Promise<{ availability: AvailabilityGrid; updated_at: string | null }> {
  const res = await fetch("/api/me/availability", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ availability: grid }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `availability update failed: ${res.status}`);
  }
  return (await res.json()) as { availability: AvailabilityGrid; updated_at: string | null };
}

export interface GroupListItem {
  id: string;
  name: string;
  sport: string;
  role: "admin" | "member";
  member_count: number;
}

export async function getMyGroups(): Promise<{ groups: GroupListItem[] }> {
  const res = await fetch("/api/groups", { credentials: "include" });
  if (!res.ok) throw new Error(`groups failed: ${res.status}`);
  return (await res.json()) as { groups: GroupListItem[] };
}

export interface CreatedClub {
  id: string;
  name: string;
  sport: string;
  join_code: string;
  default_format: "singles" | "doubles" | "mixed";
}

export async function createClub(input: {
  name: string;
  sport: string;
  default_format?: "singles" | "doubles" | "mixed";
}): Promise<CreatedClub> {
  const res = await fetch("/api/groups", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `create failed: ${res.status}`);
  }
  return (await res.json()) as CreatedClub;
}

export async function updateGroupSettings(
  groupId: string,
  body: {
    default_format?: "singles" | "doubles" | "mixed";
    digest_days?: number[];
    facility_email?: string;
    polls_admin_only?: boolean;
    visibility_standings?: "public" | "personal" | "hidden";
    session_results?: boolean;
    lock_hours?: number;
  },
): Promise<void> {
  const res = await fetch(`/api/groups/${groupId}/settings`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `settings update failed: ${res.status}`);
  }
}

// Set up a weekly recurring session series from the settings form.
export async function createRecurringSessions(
  groupId: string,
  body: {
    weekday: number; // 0=Mon..6=Sun
    time: string; // "HH:MM" 24h, club timezone
    duration_minutes: number;
    weeks: number;
    location?: string | null;
    format?: "singles" | "doubles" | null;
    court_count?: number | null;
    capacity?: number | null; // max
    min_attendance?: number | null; // min
  },
): Promise<{ created: number; first_starts_at: string | null }> {
  const res = await fetch(`/api/groups/${groupId}/recurring`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `recurring setup failed: ${res.status}`);
  }
  return (await res.json()) as { created: number; first_starts_at: string | null };
}

export interface SeriesDTO {
  id: string;
  weekday: number; // 0=Mon..6=Sun
  time: string; // "HH:MM"
  duration_minutes: number;
  location: string | null;
  format: "singles" | "doubles" | null;
  court_count: number | null;
  capacity: number | null;
  min_attendance: number | null;
  court_labels: string[] | null;
  upcoming_count: number;
}

export async function getRecurringSeries(groupId: string): Promise<SeriesDTO[]> {
  const res = await fetch(`/api/groups/${groupId}/recurring`, { credentials: "include" });
  if (!res.ok) throw new Error(`recurring list failed: ${res.status}`);
  return ((await res.json()) as { series: SeriesDTO[] }).series;
}

export async function updateRecurringSeries(
  groupId: string,
  templateId: string,
  body: {
    location?: string | null;
    format?: "singles" | "doubles" | null;
    court_count?: number | null;
    capacity?: number | null;
    min_attendance?: number | null;
    court_labels?: string[] | null;
  },
): Promise<void> {
  const res = await fetch(`/api/groups/${groupId}/recurring/${templateId}`, {
    method: "PATCH",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `series update failed: ${res.status}`);
  }
}

export async function cancelRecurringSeries(groupId: string, templateId: string): Promise<void> {
  const res = await fetch(`/api/groups/${groupId}/recurring/${templateId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `series cancel failed: ${res.status}`);
  }
}

export async function removeMember(groupId: string, userId: string): Promise<void> {
  const res = await fetch(`/api/groups/${groupId}/members/${userId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `remove failed: ${res.status}`);
  }
}

export async function setMemberRole(
  groupId: string,
  userId: string,
  role: "admin" | "member",
): Promise<void> {
  const res = await fetch(`/api/groups/${groupId}/members/${userId}/role`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ role }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `role change failed: ${res.status}`);
  }
}

export interface GroupMemberDTO {
  user_id: string;
  name: string;
  initials: string;
  role: "admin" | "member";
  skill: number | null;
  joined_at: string | null;
  is_you: boolean;
}

export interface GroupDetailDTO {
  id: string;
  name: string;
  sport: string;
  role: "admin" | "member";
  member_count: number;
  join_code: string;
  timezone: string | null;
  default_format: "singles" | "doubles" | "mixed";
  beacon_reminder_days: number[]; // weekday ints (Mon=0..Sun=6); [] = off
  lock_hours: number; // roster locks this many hours before a session
  facility_email: string | null;
  settings: Record<string, unknown>;
  members: GroupMemberDTO[];
  sessions: SessionDTO[];
  recently_played: SessionDTO[]; // most recent past session in this club (last 14 days)
}

export async function getGroupDetail(groupId: string): Promise<GroupDetailDTO> {
  const res = await fetch(`/api/groups/${groupId}`, { credentials: "include" });
  if (!res.ok) throw new Error(`groups/${groupId} failed: ${res.status}`);
  return (await res.json()) as GroupDetailDTO;
}

// Admin-only: set (or clear, with []) the group's weekly beacon-reminder days.
export async function setBeaconReminder(
  groupId: string,
  days: number[],
): Promise<{ beacon_reminder_days: number[] }> {
  const res = await fetch(`/api/groups/${groupId}/beacon-reminder`, {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ days }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `set beacon reminder failed: ${res.status}`);
  }
  return (await res.json()) as { beacon_reminder_days: number[] };
}

// Admin-only: send the pickup-beacon nudge to all members right now.
export async function sendBeaconReminderNow(
  groupId: string,
): Promise<{ sent: number }> {
  const res = await fetch(`/api/groups/${groupId}/beacon-reminder/send-now`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `send failed: ${res.status}`);
  }
  return (await res.json()) as { sent: number };
}

// Admin-only: broadcast a message to every group member (in-app + email).
export async function announceToGroup(
  groupId: string,
  body: string,
): Promise<{ sent: number }> {
  const res = await fetch(`/api/groups/${groupId}/announce`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `announce failed: ${res.status}`);
  }
  return (await res.json()) as { sent: number };
}

// ---- Per-game chat + club-level merged feed --------------------------------

export interface SessionMessageDTO {
  id: string;
  session_id: string;
  user_id: string | null; // null = ClubBot
  name: string;
  initials: string;
  is_bot: boolean;
  body: string;
  created_at: string;
}

export interface ClubMessageDTO extends SessionMessageDTO {
  game: { session_id: string; when: string; starts_at: string };
}

export async function getSessionMessages(
  sessionId: string,
  after?: string,
): Promise<SessionMessageDTO[]> {
  const qs = after ? `?after=${encodeURIComponent(after)}` : "";
  const res = await fetch(`/api/sessions/${sessionId}/messages${qs}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`load messages failed: ${res.status}`);
  return ((await res.json()) as { messages: SessionMessageDTO[] }).messages;
}

export async function postSessionMessage(
  sessionId: string,
  body: string,
): Promise<SessionMessageDTO[]> {
  const res = await fetch(`/api/sessions/${sessionId}/messages`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `send failed: ${res.status}`);
  }
  return ((await res.json()) as { messages: SessionMessageDTO[] }).messages;
}

// Read-only merged club feed: all of a group's game-chat messages, newest-first.
export async function getGroupMessages(
  groupId: string,
  before?: string,
): Promise<ClubMessageDTO[]> {
  const qs = before ? `?before=${encodeURIComponent(before)}` : "";
  const res = await fetch(`/api/groups/${groupId}/messages${qs}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`load club feed failed: ${res.status}`);
  return ((await res.json()) as { messages: ClubMessageDTO[] }).messages;
}

export async function rsvpSession(
  sessionId: string,
  intent: "yes" | "no",
): Promise<{ my_status: string }> {
  const res = await fetch(`/api/sessions/${sessionId}/rsvp`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ intent }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `rsvp failed: ${res.status}`);
  }
  return (await res.json()) as { my_status: string };
}

export async function respondToBeacon(
  beaconId: string,
  intent: "accept" | "decline",
): Promise<{ my_response: string; beacon_status: string }> {
  const res = await fetch(`/api/beacons/${beaconId}/respond`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ intent }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `beacon response failed: ${res.status}`);
  }
  return (await res.json()) as { my_response: string; beacon_status: string };
}

export async function cancelBeacon(
  beaconId: string,
): Promise<{ beacon_status: string }> {
  const res = await fetch(`/api/beacons/${beaconId}/cancel`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `beacon cancel failed: ${res.status}`);
  }
  return (await res.json()) as { beacon_status: string };
}

// ============================================================================
// Polls
// ============================================================================

export interface PollOptionDTO {
  id: string;
  text: string;
  position: number;
  count: number;
}

export interface PollDTO {
  id: string;
  group_id: string;
  question: string;
  allow_multiple: boolean;
  anonymous: boolean;
  status: "open" | "closed";
  closes_at: string | null;
  created_by: string;
  total_voters: number;
  i_voted: boolean;
  my_option_ids: string[];
  can_close: boolean;
  options: PollOptionDTO[];
}

export async function listPolls(groupId: string): Promise<{ polls: PollDTO[] }> {
  const res = await fetch(`/api/polls?group_id=${encodeURIComponent(groupId)}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`polls failed: ${res.status}`);
  return (await res.json()) as { polls: PollDTO[] };
}

export async function createPoll(body: {
  group_id: string;
  question: string;
  options: string[];
  allow_multiple: boolean;
  anonymous: boolean;
  closes_in_hours: number | null;
}): Promise<PollDTO> {
  const res = await fetch("/api/polls", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `create poll failed: ${res.status}`);
  }
  return (await res.json()) as PollDTO;
}

export async function votePoll(
  pollId: string,
  optionIds: string[],
): Promise<PollDTO> {
  const res = await fetch(`/api/polls/${pollId}/vote`, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ option_ids: optionIds }),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `vote failed: ${res.status}`);
  }
  return (await res.json()) as PollDTO;
}

export async function closePoll(pollId: string): Promise<PollDTO> {
  const res = await fetch(`/api/polls/${pollId}/close`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(err.detail ?? `close failed: ${res.status}`);
  }
  return (await res.json()) as PollDTO;
}

export interface PollVotersDTO {
  poll_id: string;
  options: {
    option_id: string;
    text: string;
    voters: { name: string; is_you: boolean }[];
  }[];
}

export async function getPollVoters(pollId: string): Promise<PollVotersDTO> {
  const res = await fetch(`/api/polls/${pollId}/voters`, { credentials: "include" });
  if (!res.ok) throw new Error(`voters failed: ${res.status}`);
  return (await res.json()) as PollVotersDTO;
}

export interface Suggestions {
  // Ordered map of category name → up-to-3 prompt strings. The server picks
  // which categories to include based on user state (no group, has group +
  // upcoming session, admin, etc.). Used for the empty-state list.
  categories: Record<string, string[]>;
  // Conversation-aware follow-up chips for the post-message "Ideas:" row,
  // generated from the recent chat. Empty/absent → fall back to `categories`.
  quick_ideas?: string[];
}

export async function getSuggestions(): Promise<Suggestions> {
  try {
    const res = await fetch("/api/chat/suggestions", { credentials: "include" });
    if (!res.ok) return { categories: {} };
    return (await res.json()) as Suggestions;
  } catch {
    return { categories: {} };
  }
}

export type ChatEvent =
  | { type: "start"; conversation_id: string }
  | { type: "delta"; text: string }
  | { type: "done"; text: string }
  | { type: "error"; message: string };

export interface HistoryMessage {
  role: "user" | "assistant";
  text: string;
}

async function fetchHistoryOnce(timeoutMs: number): Promise<HistoryMessage[] | "retry"> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch("/api/chat/history", {
      credentials: "include",
      signal: ctrl.signal,
    });
    if (res.status === 401) return [];
    // Container cold-start or transient proxy failure → retry-eligible.
    if (res.status >= 500) return "retry";
    if (!res.ok) return [];
    const data = (await res.json()) as { messages: HistoryMessage[] };
    return data.messages ?? [];
  } catch {
    // Network error or AbortError — both worth one retry.
    return "retry";
  } finally {
    clearTimeout(timer);
  }
}

export async function getChatHistory(): Promise<HistoryMessage[]> {
  // First try with a tight-ish budget; on transient failure wait briefly for
  // the agent container to finish cold-starting, then try once more.
  const first = await fetchHistoryOnce(15_000);
  if (first !== "retry") return first;
  await new Promise((r) => setTimeout(r, 2_000));
  const second = await fetchHistoryOnce(20_000);
  return second === "retry" ? [] : second;
}

function _detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export async function* streamChat(message: string): AsyncGenerator<ChatEvent> {
  const tz = _detectTimezone();
  const res = await fetch("/api/chat", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      ...(tz ? { "x-user-tz": tz } : {}),
    },
    body: JSON.stringify({ message }),
  });
  if (!res.ok || !res.body) throw new Error(`chat failed: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx = buffer.indexOf("\n\n");
    while (idx !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const event = parseSse(block);
      if (event) yield event;
      idx = buffer.indexOf("\n\n");
    }
  }
}

function parseSse(block: string): ChatEvent | null {
  let event = "";
  let data = "";
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) data += line.slice(5).trim();
  }
  if (!event) return null;
  try {
    const parsed = JSON.parse(data || "{}") as Record<string, unknown>;
    return { type: event, ...parsed } as ChatEvent;
  } catch {
    return null;
  }
}
